import { useMemo } from "react";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import type { ChannelMetrics } from "@/hooks/api/use-channel-metrics";
import { useChannelColumnSelection } from "@/hooks/use-column-selection";
import {
  DEFAULT_METRIC_ORDER,
  METRIC_CONFIG,
} from "@/pages/channels/channel-explorer/channel-metrics-config";
import { getChannelColumns } from "@/pages/channels/channel-list/channel-columns";
import {
  EntityColumnSelector,
  type SelectableMetric,
} from "./entity-column-selector";

interface ChannelsColumnSelectorProps {
  selectedMetrics?: Array<keyof ChannelMetrics>;
  onSelectMetrics?: (metrics: Array<keyof ChannelMetrics>) => void;
  showMetricsSection?: boolean;
  defaultMetrics?: Array<keyof ChannelMetrics>;
  metricOrder?: Array<keyof ChannelMetrics | string>;
  metricConfig?: Record<
    string,
    { title: string; tooltip?: string; category: string }
  >;
}

/**
 * Component for managing channel column selection including both core columns
 * and optionally metrics. Displays a popover with checkboxes for selecting which columns to display.
 */
export function ChannelsColumnSelector({
  selectedMetrics = [],
  onSelectMetrics,
  showMetricsSection = false,
  defaultMetrics = [],
  metricOrder = DEFAULT_METRIC_ORDER,
  metricConfig = METRIC_CONFIG,
}: ChannelsColumnSelectorProps) {
  // Get the core columns
  const coreColumns = getChannelColumns();

  // Use the new hook that works with both core columns and metrics
  const { availableColumns, isColumnSelected, toggleColumn, isLoading, error } =
    useChannelColumnSelection(coreColumns, selectedMetrics);

  // Filter out metric columns from availableColumns since they're handled separately
  const filteredAvailableColumns = useMemo(() => {
    return (
      availableColumns?.filter((column) => column.columnType !== "metric") || []
    );
  }, [availableColumns]);

  // Convert metrics config to SelectableMetric format
  const availableMetrics = useMemo<SelectableMetric[]>(() => {
    if (!showMetricsSection) return [];

    return metricOrder.map((metricKey) => {
      const config = metricConfig[metricKey as string];
      const categoryStyle =
        CATEGORY_STYLES[config.category as keyof typeof CATEGORY_STYLES];

      return {
        id: metricKey as string,
        title: config.title,
        tooltip: config.tooltip,
        category: config.category,
        categoryStyle: categoryStyle.color,
        categoryIcon: categoryStyle.icon,
      };
    });
  }, [showMetricsSection, metricOrder, metricConfig]);

  // Convert selected metrics to string array for the EntityColumnSelector
  const selectedMetricIds = useMemo(() => {
    return selectedMetrics.map((metric) => metric as string);
  }, [selectedMetrics]);

  // Handle metrics selection
  const handleSelectMetrics = (metricIds: string[]) => {
    if (onSelectMetrics) {
      onSelectMetrics(metricIds as Array<keyof ChannelMetrics>);
    }
  };

  return (
    <EntityColumnSelector
      availableColumns={filteredAvailableColumns}
      isColumnSelected={isColumnSelected}
      toggleColumn={toggleColumn}
      isLoading={isLoading}
      error={error}
      buttonLabel="Columns & Metrics"
      noColumnsMessage="No columns available."
      errorMessage="Error loading columns."
      // Metrics props
      availableMetrics={availableMetrics}
      selectedMetrics={selectedMetricIds}
      onSelectMetrics={handleSelectMetrics}
      showMetricsSection={showMetricsSection}
      defaultMetrics={defaultMetrics}
    />
  );
}
